// -----------------------------------------------------------------------------
//  THE DUPLICATE CHECK, ON THE PHONE
// -----------------------------------------------------------------------------
//
//  duplicate_candidates() lives in the database (migration 008) and cannot be
//  reached from a village with no signal. Losing the check offline would be a
//  quiet disaster: the whole point of registering households on foot is that
//  each dwelling appears once, and the field is exactly where two officers
//  working from opposite ends of the same street will meet in the middle.
//
//  So the rule is implemented twice — and that is a real risk, because two
//  implementations of one rule drift apart and then the app and the database
//  disagree about the same house. It is mitigated in the only way that works:
//  the same thresholds, and the SAME similarity measure. PostgreSQL's pg_trgm
//  similarity() is trigram Jaccard, and that is exactly what is written below,
//  padding included, so the two agree on real Cambodian names rather than
//  merely looking similar in a comment.
//
//  If migration 008's thresholds ever change, they change here in the same
//  commit. Nowhere else in this codebase is a rule allowed to live in two
//  places; this is the exception, and it is documented because of that.
// -----------------------------------------------------------------------------

export type LocalCandidate = {
  household_uid: string
  household_code: string | null
  head_name: string | null
  distance_m: number
  name_similarity: number | null
  severity: 'block' | 'warn' | 'info'
}

/**
 * Distance between two points on the earth, in metres.
 *
 * PostGIS uses a proper spheroid; this uses a sphere. Over the 30 metres that
 * matter here the difference is under a centimetre — far below the ±5 m the
 * GPS itself is uncertain by, so the simpler formula is not a compromise.
 */
export function haversineMetres(
  lat1: number, lon1: number, lat2: number, lon2: number,
): number {
  const R = 6371008.8 // mean earth radius, metres
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

/**
 * Fold a name the way the database's normalise_name() does — and no other way.
 *
 * This was written twice. The first version stripped Latin accents, which felt
 * obviously right: Chéa and Chea are the same person. The database does NOT do
 * that, so the phone would have scored those two names 1.00 where the server
 * scored them lower, and an officer offline would have been warned about a
 * duplicate the office never saw. Checking against PostgreSQL rather than
 * against intuition is the only reason that was caught.
 *
 * So: lower case, zero-width and non-breaking spaces turned into spaces,
 * punctuation of any script removed, whitespace collapsed. Combining marks are
 * KEPT, because in Khmer they are the vowel — folding them away would make
 * genuinely different names identical, and a duplicate check that does that
 * refuses to register real households.
 */
export function foldName(s: string): string {
  return s
    .toLowerCase()
    // zero-width space / non-joiner / joiner / non-breaking space
    .replace(/[\u200b\u200c\u200d\u00a0]/g, ' ')
    // KHMER SIGNS, U+17C9..U+17D3 plus U+17DD.
    //
    // PostgreSQL's [:alnum:] is glibc's iswalnum(), which counts Khmer
    // DEPENDENT VOWELS (U+17B6..U+17C8) as alphanumeric but not these SIGNS —
    // muusikatoan, bantoc, toandakhiat, coeng and the rest. So normalise_name()
    // silently drops them and the phone must drop exactly the same ones, or
    // ចាន់ ដារ៉ា and ចាន់ ដារា score 1.00 on the server and less than 1.00 here.
    //
    // This range was MEASURED against the database, not assumed, and
    // scripts/test-duplicates.mjs holds the evidence.
    .replace(/[\u17C9-\u17D3\u17DD]/g, '')
    // keep letters, digits, remaining combining marks and spaces
    .replace(/[^\p{L}\p{N}\p{M}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * pg_trgm's similarity(), reimplemented: the Jaccard index of the two names'
 * trigram sets, with PostgreSQL's padding — two spaces before each word and
 * one after — so short names behave the same way here as in the database.
 */
export function nameSimilarity(a: string, b: string): number {
  const grams = (s: string): Set<string> => {
    const out = new Set<string>()
    for (const word of foldName(s).split(' ').filter(Boolean)) {
      const padded = `  ${word} `
      for (let i = 0; i + 3 <= padded.length; i++) out.add(padded.slice(i, i + 3))
    }
    return out
  }

  const A = grams(a)
  const B = grams(b)
  if (A.size === 0 || B.size === 0) return 0

  let shared = 0
  for (const g of A) if (B.has(g)) shared++
  return shared / (A.size + B.size - shared)
}

export type NearbyHousehold = {
  household_uid: string
  household_code: string | null
  head_name: string | null
  lat: number | null
  lon: number | null
  record_status: string
}

/**
 * The offline half of the duplicate check. Same thresholds as migration 008:
 *
 *   under 10 m            block  — no GPS can distinguish two doorways this
 *                                  close, so the app must not let the officer
 *                                  guess. Move and capture again.
 *   under 25 m, or a
 *   name over 0.6 similar warn   — ask the officer. They are standing there;
 *                                  they can see whether it is one house.
 *   otherwise             info
 */
export function localDuplicateCandidates(
  households: NearbyHousehold[],
  lat: number,
  lon: number,
  headName: string | null,
  radiusM = 30,
): LocalCandidate[] {
  const out: LocalCandidate[] = []

  for (const h of households) {
    // EXACTLY what households_near() excludes — merged only. Not inactive.
    // Adding a condition here that the database does not have is precisely the
    // drift this file warns about: the phone would refuse a house the server
    // would accept, and the officer could never find out why.
    if (h.record_status === 'merged') continue
    if (h.lat == null || h.lon == null) continue

    const distance_m = haversineMetres(lat, lon, h.lat, h.lon)

    // The radius is a hard cut, as ST_DWithin is. A similar name RAISES the
    // severity of a household already inside the radius; it never reaches out
    // beyond it. Reaching further felt more helpful and would have been a
    // divergence — caught by comparing this file against the database itself.
    if (distance_m > radiusM) continue

    const sim =
      headName && h.head_name ? nameSimilarity(headName, h.head_name) : null

    out.push({
      household_uid: h.household_uid,
      household_code: h.household_code,
      head_name: h.head_name,
      // households_near() rounds to one decimal before comparing. Rounding
      // here too means a household at exactly 9.96 m is 'block' in both places.
      distance_m: Math.round(distance_m * 10) / 10,
      name_similarity: sim,
      severity:
        Math.round(distance_m * 10) / 10 < 10 ? 'block'
        : Math.round(distance_m * 10) / 10 < 25 || (sim ?? 0) > 0.6 ? 'warn'
        : 'info',
    })
  }

  return out.sort((a, b) => a.distance_m - b.distance_m)
}
